import { action, computed, observable } from 'mobx'
import { merge, tap } from 'rxjs'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import { FilterItemSource, FiltersViewModel } from '@/infra/FiltersViewModel'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { type Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type Bundle } from '@/features/admin/bundle/Bundle'
import { type BundleStore } from './BundleStore'
import { type BundleArchivedFilterOptions } from '@/features/admin/bundle/BundleStore'
import { type WorkspaceSearchViewModel } from './WorkspaceSearchViewModel'

export type RefreshBundlesFn = (workspace?: Workspace) => Promise<void>

export interface BundleFilter {
  workspace: Workspace | undefined
}

export class BundleSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The current search filter based on selected tab.
   */
  @observable
  accessor currentArchivedStatusFilter: BundleArchivedFilterOptions = 'Active'

  /**
   * Workspace filters used for handling bundles
   */
  readonly filters

  /**
   * The bundles of the filtered workspace, or of every workspace with bundles.
   */
  readonly scrollProvider = new InfiniteScrollProvider<Bundle>({
    limit: 200,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading bundles failed',
        signal,
      )
      return withRetry(() =>
        this._fetchAllBundles(this.workspace?.id, paging, signal),
      )
    },
  })

  constructor(
    private readonly _bundleStore: BundleStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _bundleWorkspaceSearcher: WorkspaceSearchViewModel,
  ) {
    this.filters = new FiltersViewModel<BundleFilter>({
      definitions: {
        workspace: {
          icon: 'pending',
          title: 'View:',
          implicit: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              undefined,
              // The workspace filter options are managed externally from the
              // filter view model.
              FilterItemSource.static([]),
              false,
            ),
        },
      },
    })

    this._lifecycle.manage(
      merge(
        toStream(() => this.filters.appliedFilters.workspace),
        toStream(() => this.currentArchivedStatusFilter),
      ).pipe(tap(() => this.refreshItems())),
    )
  }

  /**
   * Whether the user manages the bundles of every workspace.
   */
  @computed
  get canManageBundles() {
    return this._getWorkspaceMemberships().some(
      (wms) => wms.member.permissions.adminManageGlobalInventoryItems,
    )
  }

  /**
   * This includes all bundles from all workspaces that the user has permission in.
   */
  @computed
  get allBundles() {
    return this.scrollProvider.items
  }

  /**
   * Workspace filter
   */
  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspace') as SingleSelectFilter<
      string,
      Workspace | undefined
    >
  }

  /**
   * The workspace the bundles are filtered by.
   */
  @computed
  get workspace() {
    return this.workspaceFilter.value
  }

  /**
   * All Workspaces ids that have bundles.
   */
  @computed
  get allWorkspaceIdsWithBundles() {
    return this._bundleWorkspaceSearcher.activeWorkspaces.map(
      (workspaceWithBundle) => workspaceWithBundle.workspace.id,
    )
  }

  @action.bound
  setCurrentArchivedStatusFilter(filter: BundleArchivedFilterOptions) {
    this.currentArchivedStatusFilter = filter
  }

  @action.bound
  setWorkspace(value?: Workspace) {
    this.workspaceFilter.change(value)
  }

  @action.bound
  refreshItems() {
    // The retry alert has reported the failure.
    void this.scrollProvider
      .fetch({ signal: this._lifecycle.signal })
      .catch(() => undefined)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    this.workspaceFilter.activate()
    this.refreshItems()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.workspaceFilter.deactivate()
  }

  private async _fetchAllBundles(
    workspaceId: Id | undefined,
    paging: CursorPageQuery,
    signal: AbortSignal,
  ): Promise<CursorPaged<Bundle>> {
    if (workspaceId) {
      return await this._fetchBundles(
        workspaceId,
        paging,
        signal,
        'archived_items_count DESC',
      )
    }

    if (!this.canManageBundles) {
      return CursorPaged.empty()
    }

    // Pages through the workspaces one after another. The cursor is the
    // workspace the next page starts in, and that workspace's own cursor.
    const workspaceIds = this.allWorkspaceIdsWithBundles
    const cursor = parseAllWorkspacesCursor(paging.after)
    const startIndex = cursor ? workspaceIds.indexOf(cursor.workspaceId) : 0
    if (startIndex === -1) {
      return CursorPaged.empty()
    }

    const data: Bundle[] = []
    let after = cursor?.after
    for (let i = startIndex; i < workspaceIds.length; i++) {
      const workspaceId = workspaceIds[i]
      do {
        const page = await this._fetchBundles(
          workspaceId,
          { limit: paging.limit - data.length, after },
          signal,
        )
        data.push(...page.data)
        after = page.data.length ? (page.next ?? undefined) : undefined
      } while (after && data.length < paging.limit)

      if (after) {
        return { data, next: `${workspaceId}_${after}` }
      }

      const nextWorkspaceId = workspaceIds.at(i + 1)
      if (data.length >= paging.limit) {
        return {
          data,
          next: nextWorkspaceId ? `${nextWorkspaceId}_` : null,
        }
      }
    }

    return { data, next: null }
  }

  private async _fetchBundles(
    workspaceId: Id,
    paging: CursorPageQuery,
    signal: AbortSignal,
    orderBy = 'name',
  ) {
    return await this._bundleStore.listBundles(
      {
        ...paging,
        workspaceId,
        archivedStatus: this.currentArchivedStatusFilter,
        orderBy,
      },
      signal,
    )
  }
}

function parseAllWorkspacesCursor(cursor: string | null | undefined) {
  if (!cursor) {
    return null
  }

  const separator = cursor.indexOf('_')
  return {
    workspaceId: cursor.slice(0, separator),
    after: cursor.slice(separator + 1) || undefined,
  }
}
